/**
 * Pure maths for the doughnut view of an mc tally (THE-6011). No React, no DOM: the component only
 * draws what `donutSlices` returns, so the arithmetic is unit-tested without jsdom.
 */

export interface DonutInput {
  id: string;
  value: number;
}

export interface DonutSlice {
  id: string;
  value: number;
  /** Whole percent. Largest-remainder rounding: the slices of a non-empty tally sum to exactly 100. */
  pct: number;
  /** Degrees clockwise from 12 o'clock; `start === end` for a zero slice (nothing to draw). */
  start: number;
  end: number;
}

/**
 * One slice per input, in input order. `total` is the DENOMINATOR the bars use (`barTotal`): on a
 * priority round that is points placed, not people. When it is 0 (or the values add up to 0) every
 * slice is empty, so a round nobody answered draws no ring and no "0%" that looks like a result.
 */
export function donutSlices(items: readonly DonutInput[], total: number): DonutSlice[] {
  const values = items.map((i) => (Number.isFinite(i.value) && i.value > 0 ? i.value : 0));
  const sum = values.reduce((a, b) => a + b, 0);
  const denom = total > 0 ? Math.max(total, sum) : sum;
  if (denom <= 0) return items.map((i) => ({ id: i.id, value: 0, pct: 0, start: 0, end: 0 }));

  // Largest-remainder: floor everything, then hand the missing points to the biggest fractions.
  const exact = values.map((v) => (v / denom) * 100);
  const pcts = exact.map(Math.floor);
  // A tally that is a strict part of `denom` (counts < total) must not be forced up to 100.
  const target = Math.round((sum / denom) * 100);
  let missing = target - pcts.reduce((a, b) => a + b, 0);
  const order = exact
    .map((e, idx) => ({ idx, frac: e - Math.floor(e) }))
    .filter((x) => values[x.idx]! > 0)
    .sort((a, b) => b.frac - a.frac || a.idx - b.idx);
  for (let k = 0; missing > 0 && order.length > 0; k = (k + 1) % order.length, missing--) {
    pcts[order[k]!.idx]! += 1;
  }

  let acc = 0;
  return items.map((i, idx) => {
    const sweep = (values[idx]! / denom) * 360;
    const slice: DonutSlice = { id: i.id, value: values[idx]!, pct: pcts[idx]!, start: acc, end: acc + sweep };
    acc += sweep;
    return slice;
  });
}

function point(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

const f = (n: number): string => n.toFixed(3);

/**
 * SVG path for one ring segment, in viewBox units. A full turn is drawn as two half arcs because a
 * single arc whose start and end coincide is a no-op in SVG. Empty slice → "".
 */
export function ringPath(cx: number, cy: number, rOuter: number, rInner: number, start: number, end: number): string {
  const sweep = end - start;
  if (sweep <= 0) return "";
  if (sweep >= 359.999) {
    const mid = start + 180;
    return `${ringPath(cx, cy, rOuter, rInner, start, mid)} ${ringPath(cx, cy, rOuter, rInner, mid, start + 360)}`;
  }
  const [x1, y1] = point(cx, cy, rOuter, start);
  const [x2, y2] = point(cx, cy, rOuter, end);
  const [x3, y3] = point(cx, cy, rInner, end);
  const [x4, y4] = point(cx, cy, rInner, start);
  const large = sweep > 180 ? 1 : 0;
  return `M${f(x1)} ${f(y1)} A${f(rOuter)} ${f(rOuter)} 0 ${large} 1 ${f(x2)} ${f(y2)} L${f(x3)} ${f(y3)} A${f(rInner)} ${f(rInner)} 0 ${large} 0 ${f(x4)} ${f(y4)} Z`;
}
